import { Box, FormControl, InputLabel, MenuItem, Select } from "@mui/material";
import { ContactType, ProjectType } from "@repo/server-api";
import { useBoundDeep } from "@repo/shared/common";
import { BoundAsyncSelect, BoundDateField, BoundTextField, useAsyncSelectContactQuery, useAsyncSelectProjectQuery } from "@repo/shared/components";
import { useTranslation } from "next-i18next";
import React from "react";

import { updateField } from "@repo/shared-project/types/ProjectTypes";

import type { SelectChangeEvent } from "@mui/material/Select";
import type { ErrorMap} from "@repo/shared/common";
import type { NewProject} from "@repo/shared-project/types/ProjectTypes";
import type { SetStateAction } from "react";

type ProjectCreateProps = {
	project: NewProject;
	setProject: (value: SetStateAction<NewProject>) => void;
	errors: ErrorMap;
	setErrors: (value: SetStateAction<ErrorMap>) => void;
	contactIsReadOnly: boolean;
}

const ProjectCreate = (props: ProjectCreateProps) => {
	const { t } = useTranslation();
	const { project, setProject, errors, setErrors, contactIsReadOnly } = props;

	const useBinding = <TValue,>(selector: (m: NewProject) => TValue) => {
		return useBoundDeep(project, setProject, selector, errors, setErrors);
	};

	const projectNameField = useBinding((p) => p.name);
	const projectStartDateField = useBinding((p) => p.startDate);
	const projectEndDateField = useBinding((p) => p.endDate);
	const contractorField = useBinding((p) => p.contractor);
	const clientField = useBinding((p) => p.client);
	const parentProjectField = useBinding((p) => p.parentProject);

	const searchContractorHook = useAsyncSelectContactQuery([ContactType.Contractor]);
	const searchClientHook = useAsyncSelectContactQuery([ContactType.Client]);
	const searchTenderHook = useAsyncSelectProjectQuery(ProjectType.Tender);

	const handleProjectTypeChange = (event: SelectChangeEvent<ProjectType | "">): void => {
		updateField(setProject, "type", event.target.value as ProjectType);
	};

	return (
		<div>
			<Box className="fullCenter" sx={{ mt: 2.5 }}>
				<FormControl fullWidth>
					<InputLabel id="project-type-label">{t("type")}</InputLabel>
					<Select<ProjectType | "">
						labelId="project-type-label"
						id="select-project-type"
						value={project.type}
						label={t("type")}
						onChange={handleProjectTypeChange}>
						<MenuItem value={ProjectType.Project}>{t("project")}</MenuItem>
						<MenuItem value={ProjectType.Tender}>{t("tender")}</MenuItem>
						<MenuItem value={ProjectType.Internal}>{t("internal-project")}</MenuItem>
					</Select>
				</FormControl>
			</Box>

			<Box className="fullCenter" sx={{ mt: 2.5 }}>
				<BoundTextField
					label={t("name")}
					boundedValue={projectNameField}
					additionalProps={{
						sx: { float: "left", width: "100%" },
						variant: "outlined",
					}}
				/>
			</Box>

			{(project.type === ProjectType.Project || project.type === ProjectType.Internal) ? <Box className="fullCenter" sx={{ mt: 2.5 }}>
					<BoundAsyncSelect
						label={t("tender")}
						boundedValue={parentProjectField}
						searchHook={searchTenderHook}
						additionalProps={{ className: "w-100 left", readOnly: contactIsReadOnly }}
					/>
				</Box> : null}

			<Box className="fullCenter" sx={{ mt: 2.5 }}>
				<BoundAsyncSelect
					label={t("contractor")}
					boundedValue={contractorField}
					searchHook={searchContractorHook}
					additionalProps={{ className: "w-100 left", readOnly: contactIsReadOnly }}
				/>
			</Box>

			{project.type !== ProjectType.Internal && <Box className="fullCenter" sx={{ mt: 2.5 }}>
				<BoundAsyncSelect
					label={t("client")}
					boundedValue={clientField}
					searchHook={searchClientHook}
					additionalProps={{ className: "w-100 left", readOnly: contactIsReadOnly }}
				/>
			</Box>}

			<Box className="fullCenter" sx={{ m: 1, mt: 2.5 }}>
				<BoundDateField label={t("from")} boundedValue={projectStartDateField} />
				<span className="font50 font2">-</span>
				<BoundDateField label={t("to")} boundedValue={projectEndDateField} />
			</Box>
		</div>
	);
};

ProjectCreate.displayName = "ProjectCreate";
export { ProjectCreate };
